import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import type { ScribeApiClient, TokenProvider } from '@scribe/core/client';
import { createApiClient, createTokenProvider } from './apiClient';
import { isMock, mockUser } from './mock';
import { isLocalAuth, localAuthUser } from './localAuth';

export interface ApiContextValue {
  client: ScribeApiClient;
  displayName: string | null;
  email: string | null;
  /** Same token *logic* `createApiClient` uses for `client` — an equivalent
   * `createTokenProvider` closure over the shared MSAL cache (not literally the same instance),
   * so mock/local-auth/real-Entra token acquisition is identical between REST and the streaming
   * bridge's WS `?token=`. Keep `createTokenProvider` stateless for this equivalence to hold. */
  getToken: TokenProvider;
  /** Streaming-transcription bridge WS endpoint (`VITE_STREAMING_BRIDGE_URL`), or `undefined`
   * if unconfigured — `useStreamingSession` treats that as "streaming unavailable". */
  streamingBridgeUrl: string | undefined;
}

// Exported (not just useApi/ApiProvider) so tests can render screens/hooks against a fake
// client without constructing a real MSAL instance — see src/test/testUtils.tsx.
export const ApiContext = createContext<ApiContextValue | null>(null);

export function ApiProvider({ children }: { children: ReactNode }) {
  const { instance, accounts } = useMsal();

  const value = useMemo<ApiContextValue>(() => {
    const client = createApiClient(instance);
    // A second createTokenProvider(instance) closure — deliberately equivalent to the one
    // createApiClient bound `client` to (same logic, same shared MSAL token cache), so the
    // streaming bridge's `?token=` and the REST client's Bearer header always agree
    // (mock-token / LOCAL_AUTH_TOKEN / real Entra alike). Both are stateless; keep it that way.
    const getToken = createTokenProvider(instance);
    const streamingBridgeUrl = import.meta.env.VITE_STREAMING_BRIDGE_URL;

    if (isMock()) {
      return {
        client,
        displayName: mockUser.profile.name,
        email: mockUser.profile.email,
        getToken,
        streamingBridgeUrl,
      };
    }
    if (isLocalAuth()) {
      return {
        client,
        displayName: localAuthUser.displayName,
        email: localAuthUser.email,
        getToken,
        streamingBridgeUrl,
      };
    }

    const account = accounts[0];
    // Identity for display purposes only — the durable authorization key is the `oid` claim
    // on the *access token* the backend validates server-side; the web app never needs to
    // read or trust `oid` locally (unlike the earlier Cognito setup, which had to reason
    // about which token carried which claims).
    return {
      client,
      displayName: account?.name ?? null,
      email: account?.username ?? null,
      getToken,
      streamingBridgeUrl,
    };
  }, [instance, accounts]);

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
}

export function useApi(): ApiContextValue {
  const ctx = useContext(ApiContext);
  if (!ctx) throw new Error('useApi must be used within an ApiProvider');
  return ctx;
}
